import { Node } from "@xyflow/react";

import { NodeData } from "../types/node-data.types";

/** The same bound `resolveEmailSubject` and `validateCampaignConfig` walk the canvas under. */
const MAX_ITERATIONS = 100;

/**
 * Whether any step before `nodeId` is of `type` — "is there an email above this one?", which is
 * what decides whether a step may inherit a reply subject.
 *
 * Takes `getPreviousNode` as a callback, like `resolveEmailSubject`, and carries the same `visited`
 * set and iteration bound. That guard is not decorative: the walk this replaced stopped only on a
 * `starterNode`, so a canvas holding an edge cycle with no reachable starter — which the builder
 * lets you draw — spun forever on the render thread.
 */
export const hasPrecedingNodeOfType = (
	nodeId: string,
	getPreviousNode: (id: string) => Node<NodeData> | null,
	type: string
): boolean => {
	const visited = new Set<string>();

	let cursor: Node<NodeData> | null = getPreviousNode(nodeId);
	let iterations = 0;

	while (cursor && iterations < MAX_ITERATIONS) {
		if (visited.has(cursor.id)) {
			return false;
		}

		visited.add(cursor.id);

		if (cursor.type === type) {
			return true;
		}

		if (cursor.type === "starterNode") {
			return false;
		}

		cursor = getPreviousNode(cursor.id);
		iterations += 1;
	}

	return false;
};
